import React, { Component } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import ui from 'redux-ui';
import PropTypes from 'prop-types';
import qs from 'qs';
import cx from 'classnames';
import { isEqual, omit, memoize } from 'lodash';
import { CUT_POINT, REFRESH_INTERVAL,
         RESTRICTED_FILTER_POSITIONS } from 'config/constants';
import { PhaseIndexType } from 'types/phase';
import { can } from '../../../../../services';
import { resetPhasePagination, requestPhaseList, requestPhasesForExport,
         requestEmployee, navigate, requestSavedFilters, requestAssignedFilters,
         deleteSavedFilter, clearValidationErrors } from 'actionCreators';
import { replace } from 'react-router-redux';
import { getRole, getEmployableId, getEmployableType, getPhaseHasNextPage,
         getPhasesLoading, getPhaseFiltersData, getPhaseFiltersLocation, getWorkroomList,
         getPosition, getManageVendorWorkrooms } from '../../../../../reducers';
import { urls } from 'helpers/url';
import { Drawer } from 'react-toolbox';
import ConfirmDialog from 'components/ConfirmDialog';
import PageActionBar from 'components/PageActionBar';
import PhaseFilters from 'PhaseFilters';
import PhaseListHeader from 'PhaseListHeader';
import SavedFiltersList from 'SavedFiltersList';
import PhaseCard from 'phaseCard';
import NoResults from '../components/noResults';
import MessageBox from 'components/MessageBox';
import InfiniteLoading from 'components/InfiniteLoading';
import Infinite from 'react-infinite';
import MediaQuery from 'react-responsive';
import contentAreaStyles from 'styles/contentArea';

// Quick filter keys that should not trigger the PhaseFilters drawer
const QUICK_FILTER_KEYS = ['assigned_employee'];

class PhaseList extends Component {
  state = {
    canViewInactive: can(this.props.role, 'view', 'inactive'),
    currentFilter: null,
    editing: false,
    showConfirm: false,
    savedFilterName: '',
    sidebarOpen: false,
    modalNumber: 1,
    renderExcel: false,
  };

  componentWillMount = () => {
    const { position, employableId, employableType } = this.props;
    this.requestFilters(position, true);
    if (employableType === 'employee') this.props.requestEmployee(employableId);
  };

  componentDidMount = () => {
    const { position, role, employableType } = this.props;
    const validSavedFilterUser = this.isValidFilterUser(employableType, role);
    if (['employee', 'iqm'].includes(employableType)) this.setRefreshInterval('assigned');
    if (validSavedFilterUser && !this.restrictFilters(position)) this.setRefreshInterval('saved');
  }

  componentWillReceiveProps = (newProps) => {
    const { position, location, workrooms } = this.props;
    const { employableType } = newProps;
    if (employableType === 'employee' &&
        position !== newProps.position) this.requestFilters(newProps.position, false, true, true);

    // Check if workroom filters changed and clear supervisor if no IFI workroom remains
    if (newProps.location.search !== location.search) {
      // Parse both old and new filters from URL to ensure accurate comparison
      const prevFilters = this.parseUrlFilters(location.search);
      const newFilters = this.parseUrlFilters(newProps.location.search);

      const prevWorkroomIds = this.parseWorkroomIds(prevFilters);
      const newWorkroomIds = this.parseWorkroomIds(newFilters);

      // Check if we had IFI workrooms before and don't have any now
      const prevHasIfi = this.hasIfiWorkroom(workrooms, prevWorkroomIds);
      const newHasIfi = this.hasIfiWorkroom(newProps.workrooms, newWorkroomIds);

      if (prevHasIfi && !newHasIfi && newFilters.assigned_employee) {
        // Clear the supervisor filter
        delete newFilters.assigned_employee;
        this.updatePathWithFilters(newFilters, 'replace');
        return;
      }

      this.updateFilters(newProps);
    }
    if (newProps.exportPhases !== this.props.exportPhases) {
      this.setState({ renderExcel: true });
      if (!this.state.showConfirm) this.toggleModal(3);
    }
  }

  componentWillUnmount = () => {
    if (this.props.ui.successMessage) this.props.updateUI('successMessage', '');
    if (this.refreshSavedFiltersInterval) clearInterval(this.refreshSavedFiltersInterval);
    if (this.refreshAssignedFiltersInterval) clearInterval(this.refreshAssignedFiltersInterval);
  };

  onSearch = (e) => {
    e.preventDefault();
    this.updatePathWithFilters({ query: e.target.query.value.trim() || undefined });
    if (this.state.currentFilter) this.setState({ currentFilter: null });
  };

  onFilterChange = newFilters => this.updatePathWithFilters(newFilters, 'replace');

  onSupervisorChange = (employeeId) => {
    // Parse current URL params to preserve existing filters
    const currentFilters = this.parseUrlFilters(this.props.location.search);

    const newFilters = { ...currentFilters };
    if (employeeId) {
      newFilters.assigned_employee = employeeId;
    } else {
      delete newFilters.assigned_employee;
    }

    // Clear current filter selection when using quick filter
    this.setState({ currentFilter: null, editing: false });
    this.updatePathWithFilters(newFilters, 'replace');
  };

  onFilterSelect = (filter) => {
    this.setState({ currentFilter: filter });
    this.updatePathWithFilters(filter.filters);
  };

  onStopEdit = (filter) => {
    this.setState({ editing: false, copiedFrom: null, savedFilterName: '' });
    this.props.clearValidationErrors('SAVED_FILTER');
    if (filter) this.onFilterSelect(filter);
    else if (this.state.currentFilter) this.onFilterSelect(this.state.currentFilter);
    else if (this.state.copiedFrom) this.onFilterSelect(this.state.copiedFrom);
    else this.setCurrentFilter();
  };

  setRefreshInterval = (filter) => {
    if (filter === 'saved') {
      this.refreshSavedFiltersInterval = setInterval(
        () => this.props.requestSavedFilters(), REFRESH_INTERVAL,
      );
    } else {
      this.refreshAssignedFiltersInterval = setInterval(
        () => this.props.requestAssignedFilters(), REFRESH_INTERVAL,
      );
    }
  }

  setCurrentFilter = () => {
    if (this.state.editing) return;
    const {
      position, savedFilters, assignedFilters, editableFilters,
    } = this.props;
    const restricted = this.restrictFilters(position);

    // Exclude quick filter keys when checking if we should open editing mode
    // Quick filters (assigned_employee, supervisor) should not trigger the PhaseFilters drawer
    const nonQuickFilters = this.getNonQuickFilters(editableFilters);

    // First, check if there's a matching saved/assigned filter
    const selected = restricted
      ? assignedFilters.filters.find(filter => isEqual(filter.filters, editableFilters))
      : (savedFilters.filters.find(filter => isEqual(filter.filters, editableFilters)) ||
          assignedFilters.filters.find(filter => isEqual(filter.filters, editableFilters)));

    if (selected) {
      this.setState({ currentFilter: selected, editing: false });
      return;
    }

    // Don't open drawer if only quick filters are present (and no matching saved filter)
    if (!nonQuickFilters.length) {
      this.setState({ currentFilter: null, editing: false });
      return;
    }

    // Open drawer if there are non-quick filters (user can create a new saved filter)
    this.setState({ editing: true });
  };

  getNonQuickFilters = editableFilters => Object.keys(editableFilters)
    .filter(key => !QUICK_FILTER_KEYS.includes(key));

  isValidFilterUser = (employableType, role) => {
    if (employableType === 'employee' || ['installer', 'estimator'].includes(role.toLowerCase())) return true;
    return false;
  }

  restrictFilters = position => RESTRICTED_FILTER_POSITIONS.includes(position);

  requestFilters = (
    position, onWillMount = false, setSavedRefresh = false, setAssignedRefresh = false,
  ) => {
    const { savedFilters, assignedFilters } = this.props;
    const restricted = this.restrictFilters(position);
    const loadedCondition = restricted ? assignedFilters.loaded :
      savedFilters.loaded && assignedFilters.loaded;
    if (setAssignedRefresh) this.setRefreshInterval('assigned');
    if (setSavedRefresh && !restricted) this.setRefreshInterval('saved');
    if (loadedCondition) {
      this.initialLoad();
      if (!restricted || onWillMount) this.props.requestSavedFilters();
      this.props.requestAssignedFilters();
    } else if (!onWillMount && restricted) {
      Promise.all([this.props.requestAssignedFilters()]).then(() => this.initialLoad());
    } else {
      Promise.all([this.props.requestSavedFilters(), this.props.requestAssignedFilters()])
        .then(() => this.initialLoad());
    }
  }

  IGNORE_LOCATION_FILTERS = window.ignoreLocationFilters || false;

  canAccessSupervisorFilter = () => {
    const {
      manageVendorWorkrooms,
      role,
      position,
      workrooms,
      phaseFiltersLocation,
    } = this.props;

    // Must have at least one IFI workroom selected
    const selectedWorkroomIds = (phaseFiltersLocation && phaseFiltersLocation.workroom) || [];
    if (!this.hasIfiWorkroom(workrooms, selectedWorkroomIds)) {
      return false;
    }

    // Check supervisor filter access using ability.js
    return can(role, 'view', 'supervisor_filter', {
      manageVendorWorkrooms: manageVendorWorkrooms || {},
      position: position || '',
    });
  };

  hasIfiWorkroom = (workrooms, workroomIds) => workrooms && workroomIds.length > 0 &&
    workrooms.some(w => w.vendor === 'IFI' && workroomIds.includes(w.id));

  parseUrlFilters = search => (search ? qs.parse(search.slice(1), { arrayLimit: 1000 }) : {});

  parseWorkroomIds = filters => (filters.workroom || []).map(id => parseInt(id, 10));

  initialLoad = () => {
    const { position, savedFilters, assignedFilters, location } = this.props;
    const restricted = this.restrictFilters(position);
    const filterLengthCondition = restricted ? !assignedFilters.filters.length
      : !(savedFilters.filters.length || assignedFilters.filters.length);
    if (filterLengthCondition) {
      return this.updateFilters(this.props);
    } else if (location.search) {
      this.setCurrentFilter();
      return this.updateFilters(this.props);
    } else if (assignedFilters.filters.length || restricted) {
      this.setState({ currentFilter: assignedFilters.filters[0] });
      return this.onFilterChange(assignedFilters.filters[0].filters);
    }
    this.setState({ currentFilter: savedFilters.filters[0] });
    return this.onFilterChange(savedFilters.filters[0].filters);
  };

  updatePathWithFilters = (newFilters, method = 'navigate') => this.props[method](urls.phases({ query: newFilters }));

  updateFilters = (props = this.props, call = true) => {
    let promise = null;
    if (!props.location.search) {
      if (!this.state.editing) this.setState({ currentFilter: null });
      promise = this.props.resetPhasePagination({});
    } else {
      const filterObject = this.parseUrlFilters(props.location.search);
      ['workroom', 'store', 'region', 'surface_type', 'estimator'].forEach((prop) => {
        if (filterObject[prop]) filterObject[prop] = filterObject[prop].map(id => parseInt(id, 10));
      });
      if (filterObject.assigned_employee) {
        filterObject.assigned_employee = parseInt(filterObject.assigned_employee, 10);
      }
      promise = this.props.resetPhasePagination(filterObject);
    }
    this.setState({ sidebarOpen: false });
    if (call) {
      promise = this.props.requestPhaseList(this.IGNORE_LOCATION_FILTERS)
        .then(this.setCurrentFilter);
    }
    return promise || Promise.resolve();
  };

  addAction = () => this.props.navigate(urls.createPhase());

  viewAction = id => this.props.navigate(urls.phase({ id }));

  newFilter = () => {
    this.props.navigate(urls.phases());
    this.setState({ currentFilter: null }, this.editFilter);
  };

  toggleFilters = memoize(isDesktop => () => {
    if (this.state.editing) this.onStopEdit();
    else if (isDesktop) this.newFilter();
    else this.toggleMobileSidebar();
  });

  toggleMobileSidebar = () => this.setState({ sidebarOpen: !this.state.sidebarOpen });

  deleteFilter = () => {
    const { currentFilter } = this.state;
    if (currentFilter.type !== 'saved') return;
    this.props.deleteSavedFilter(currentFilter.id).then(() => {
      this.setState({ currentFilter: null, editing: false, showConfirm: false });
      this.updatePathWithFilters({});
    });
  };

  handleFilterName = value => this.setState({ savedFilterName: value });

  copyFilter = () => {
    const copiedFrom = this.state.currentFilter;
    this.setState({ editing: true, copiedFrom, currentFilter: null, savedFilterName: '' });
  };

  editFilter = () => this.setState({
    editing: true, savedFilterName: this.state.currentFilter ? this.state.currentFilter.name : '',
  });

  handleInfiniteLoad = () => (this.props.needsMore && this.props.savedFilters.loaded ?
      this.props.requestPhaseList(this.IGNORE_LOCATION_FILTERS) : null);

  toggleModal = modalNumber => this.setState({ showConfirm: !this.state.showConfirm, modalNumber });

  fetchExportPhases = (page) => {
    let pageNumber = 1;
    const n = parseInt(page, 10);
    if (n) pageNumber = n;
    this.props.requestPhasesForExport(pageNumber);
    this.toggleModal(3);
  };

  unmountExcelSheet = () => this.setState({ renderExcel: false });

  render = () => {
    const { phases, exportPhases, role, position, employableType, needsMore, loading,
            editableFilters, filters, total_count: totalCount } = this.props;
    const { editing, currentFilter, showConfirm, sidebarOpen,
            modalNumber, renderExcel } = this.state;
    const UI = this.props.ui;
    const contentSidebar = cx({
      [contentAreaStyles.contentSidebar]: true,
      [contentAreaStyles.contentSidebarEditing]: this.state.editing,
    });
    const hidePhaseFilters = !this.isValidFilterUser(employableType, role)
      || this.restrictFilters(position);
    let phaseList = phases.map(phase => (<PhaseCard
      key={`phase-${phase.id}`}
      role={role}
      position={position}
      phase={phase}
      onSelect={this.viewAction}
      surfaceType={phase.surface_type}
      store={phase.store}
    />));
    if (!phaseList.length && !loading) phaseList = <NoResults />;
    // Exclude quick filters when determining if we should show "New Filter"
    const nonQuickFilters = this.getNonQuickFilters(editableFilters);
    let title = nonQuickFilters.length ? 'New Filter' : 'All Phases';
    if (filters && filters.query) title = 'Search Results';
    if (currentFilter) title = currentFilter.name;
    const isSavedCurrent = currentFilter && currentFilter.type === 'saved';
    return (<MediaQuery minWidth={CUT_POINT}>
      {isDesktop => (<div>
        <PageActionBar
          resource={'phase'}
          role={role}
          hidesAddOnMobile
          addAction={this.addAction}
          showFiltersBtn={!this.IGNORE_LOCATION_FILTERS}
          userRestrictedFilters={isDesktop && hidePhaseFilters}
          toggleFilters={this.toggleFilters(isDesktop)}
          title={isDesktop ? 'Phase List' : title}
          banner={this.IGNORE_LOCATION_FILTERS ? '(This window ignores location filters)' : ''}
          searchText={isDesktop ?
            'Search by customer name, PO number, project number, address, phone...' :
            'Search Phases'}
          searchValue={this.props.filters.query}
          onSearch={this.onSearch}
        />
        <div className={contentAreaStyles.containerWithSidebar}>
          {UI.successMessage && <MessageBox message={UI.successMessage} />}
          {isDesktop && (<div className={contentAreaStyles.contentWithSidebar}>
            <aside className={contentSidebar}>
              {!hidePhaseFilters && editing && (<PhaseFilters
                filters={editableFilters}
                onFilterChange={this.onFilterChange}
                savedFilterName={this.state.savedFilterName}
                onStopEdit={this.onStopEdit}
                currentFilter={currentFilter}
                handleFilterName={this.handleFilterName}
              />)}
              {(!editing || hidePhaseFilters) && (<SavedFiltersList
                selected={currentFilter}
                onFilterSelect={this.onFilterSelect}
                ignoreLocationFilters={this.IGNORE_LOCATION_FILTERS}
                hideSaved={hidePhaseFilters}
                role={role}
                filters={filters}
                onSupervisorChange={this.onSupervisorChange}
                canAccessSupervisorFilter={this.canAccessSupervisorFilter()}
              />)}
            </aside>
            <div className={contentAreaStyles.contentAreaWrapper}>
              <div className={contentAreaStyles.contentArea}>
                <PhaseListHeader
                  currentFilter={currentFilter}
                  deleteFilter={isSavedCurrent ? this.deleteFilter : undefined}
                  editFilter={isSavedCurrent ? this.editFilter : undefined}
                  copyFilter={isSavedCurrent ? this.copyFilter : undefined}
                  toggleModal={this.toggleModal}
                  modalNumber={modalNumber}
                  renderExcel={renderExcel}
                  onProceed={this.fetchExportPhases}
                  unmountExcelSheet={this.unmountExcelSheet}
                  handleEditStatus={this.handleEditStatus}
                  showConfirm={showConfirm}
                  title={title}
                  loading={loading}
                  totalCount={totalCount}
                  phases={exportPhases || []}
                />
                {modalNumber === 3 ? (<ConfirmDialog
                  theActions={['OK']}
                  title="Fetching export data from server..."
                  confirmation="This may take a minute..."
                  show={!showConfirm}
                  dismiss={() => this.toggleModal(3)}
                  proceed={() => this.toggleModal(3)}
                />) : null}
                <Infinite
                  useWindowAsScrollContainer
                  elementHeight={163}
                  infiniteLoadBeginEdgeOffset={0}
                  onInfiniteLoad={this.handleInfiniteLoad}
                  isInfiniteLoading={loading}
                >{phaseList}</Infinite>
                {needsMore && <InfiniteLoading />}
              </div>
            </div>
          </div>)}
          {!isDesktop && (<div>
            <Drawer active={sidebarOpen} onOverlayClick={this.toggleMobileSidebar}>
              <SavedFiltersList
                selected={currentFilter}
                onFilterSelect={this.onFilterSelect}
                ignoreLocationFilters={this.IGNORE_LOCATION_FILTERS}
                hideSaved={hidePhaseFilters}
                role={role}
              />
            </Drawer>
            <Infinite
              useWindowAsScrollContainer
              elementHeight={305}
              infiniteLoadBeginEdgeOffset={0}
              onInfiniteLoad={this.handleInfiniteLoad}
              isInfiniteLoading={loading}
            >{phaseList}</Infinite>
            {needsMore && <InfiniteLoading />}
          </div>)}
        </div>
      </div>)}
    </MediaQuery>);
  }
}

PhaseList.propTypes = {
  phases: PropTypes.arrayOf(PhaseIndexType).isRequired,
  exportPhases: PropTypes.array,
  total_count: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    query: PropTypes.string,
  }),
  editableFilters: PropTypes.object,
  savedFilters: PropTypes.shape({
    loaded: PropTypes.bool.isRequired,
    filters: PropTypes.array.isRequired,
  }).isRequired,
  assignedFilters: PropTypes.shape({
    loaded: PropTypes.bool.isRequired,
    filters: PropTypes.array.isRequired,
  }).isRequired,
  needsMore: PropTypes.bool,
  role: PropTypes.string,
  employableId: PropTypes.number,
  employableType: PropTypes.string,
  position: PropTypes.string,
  manageVendorWorkrooms: PropTypes.shape({
    ifi: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  }),
  workrooms: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    vendor: PropTypes.string,
  })),
  phaseFiltersLocation: PropTypes.shape({
    workroom: PropTypes.arrayOf(PropTypes.number),
    region: PropTypes.arrayOf(PropTypes.number),
  }),
  loading: PropTypes.bool,
  ui: PropTypes.shape({
    successMessage: PropTypes.string,
  }),
  updateUI: PropTypes.func,
  resetPhasePagination: PropTypes.func.isRequired,
  requestPhaseList: PropTypes.func.isRequired,
  requestPhasesForExport: PropTypes.func.isRequired,
  requestSavedFilters: PropTypes.func.isRequired,
  requestAssignedFilters: PropTypes.func.isRequired,
  requestEmployee: PropTypes.func.isRequired,
  deleteSavedFilter: PropTypes.func.isRequired,
  clearValidationErrors: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  replace: PropTypes.func.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }).isRequired,
};

const getEditableFilters = createSelector(getPhaseFiltersData, filters => omit(filters, ['query']));

const mapStateToProps = (state) => {
  const employableType = getEmployableType(state).toLowerCase();
  const employableId = getEmployableId(state);
  return {
    phases: state.phases.results,
    exportPhases: state.phases.exportResults,
    total_count: state.phases.pagination.total_count,
    filters: state.phases.filters.data,
    editableFilters: getEditableFilters(state),
    phaseFiltersLocation: getPhaseFiltersLocation(state),
    savedFilters: state.savedFilters,
    assignedFilters: state.assignedFilters,
    needsMore: getPhaseHasNextPage(state),
    loading: getPhasesLoading(state),
    role: getRole(state),
    position: getPosition(state),
    manageVendorWorkrooms: getManageVendorWorkrooms(state),
    workrooms: getWorkroomList(state),
    employableType,
    employableId,
  };
};

export default ui()(connect(
  mapStateToProps, {
    resetPhasePagination,
    requestPhaseList,
    requestPhasesForExport,
    requestSavedFilters,
    requestAssignedFilters,
    requestEmployee,
    navigate,
    deleteSavedFilter,
    clearValidationErrors,
    replace,
  })(PhaseList));



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseList/index.jsx